import { RegisterClass } from "core/Misc/typeStore";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

export enum RewardFloatConstantType {
    DeltaTime,
    GravityMagnitude,
    PI
}

function OperationString(operation : RewardFloatConstantType) : string {
    if( typeof operation === 'number' )
        return RewardFloatConstantType[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardFloatConstantType as any)[op];
}
/**
 * Block used to apply math functions
 */
export class RewardFloatConstant extends RewardGraphNode {
    /**
     * Gets or sets the operation applied by the block
     */
    @editableInPropertyPage("Property", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "DeltaTime", value: RewardFloatConstantType.DeltaTime },
            { label: "GravityMagnitude", value: RewardFloatConstantType.GravityMagnitude },
            { label: "PI", value: RewardFloatConstantType.PI },
        ],
    })
    public property = RewardFloatConstantType.DeltaTime;

    /**
     * Create a new MathBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerOutput("value", RewardGraphConnectionPointTypes.Float);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardFloatConstant";
    }

    /**
     * Gets the left input component
     */
    public get value(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        let func: (state: RewardGraphBuildState) => any;

        switch (this.property) {
            case RewardFloatConstantType.DeltaTime: {
                func = (state) => {
                    if( state.queryContext ) 
                        return state.queryContext.getGlobalFloatProperty('delta-time')
                    return 1/30.0;
                };
                break;
            }
            case RewardFloatConstantType.GravityMagnitude: {
                func = (state) => {
                    if( state.queryContext ) 
                        return state.queryContext.getGlobalFloatProperty('gravity-magnitude')
                    return 1/30.0;
                };
                break;
            }
            case RewardFloatConstantType.PI: {
                func = (state) => {
                    return Math.PI;
                };
                break;
            }
        }

        this.value._storedFunction = (state) => {
            return func(state);
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.operation = MELS.RewardFloatConstant.${RewardFloatConstantType[this.property]};\n`;
        return codeString;
    }

    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.property = OperationString(this.property);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.property = StringOperation(serializationObject.property);
    }
}

RegisterClass("MELS.RewardFloatConstant", RewardFloatConstant);
